import { Autocomplete, TextField, Tooltip } from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce, useError } from '../helpers/hooks';

interface IXsAutocompleteComplete extends Omit<ControllerRenderProps, 'ref'> {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  request: {
    uri: string;
    payload: {
      search: (value: string) => any;
    };
    includeToken?: boolean;
  };
  identFn: (option: any) => string | number | object;
  labelFn: (option: any) => string;
  onApply?: (option: any, isLoad: boolean) => void;
  minLengthForSearch?: number;
  noOptionsText?: string;
  disableLoad?: boolean;
  placeholder?: string;
  sortDataList?: (data: any) => any;
  forceSearchString?: string;
  forceSearchSetValue?: any;
  clearForceSearchValue?: () => void;
  onSearchAction?: (data: any) => any;
  getNoOptionsTextFromState?: (state: XsAutocompleteWithoutLoadSearchingStateTypes) => string;
  searchAfterForceSetValue?: (adress: string) => any;
  resetValue?: number;
}

export enum XsAutocompleteWithoutLoadSearchingStateTypes {
  WATINGFORINPUT = 'watingForInput',
  CHARACTERMINLENGTH = 'characterMinLength',
  WATINGFORDELAY = 'watingForDelay',
  WATINGFORDB = 'watingForDB',
  SHOWDATA = 'showData',
  NODATA = 'noData',
  NOSEARCH = 'noSearch'
}

export const XsAutocompleteServerWithoutLoad = (props: IXsAutocompleteComplete) => {
  const {
    value,
    name,
    request,
    identFn,
    labelFn,
    onApply,
    label,
    required = false,
    disabled,
    noOptionsText,
    minLengthForSearch = 0,
    placeholder,
    sortDataList,
    forceSearchString,
    forceSearchSetValue,
    onSearchAction,
    getNoOptionsTextFromState,
    clearForceSearchValue,
    searchAfterForceSetValue,
    resetValue
  } = props;
  const { uri, payload, includeToken = true } = request;
  const { search } = payload;
  const axiosConfig = includeToken ? undefined : { headers: { Authorization: '' } };

  const { t } = useTranslation();
  const form = useFormContext();
  const { error, helperText } = useError(name);
  const inputReference = useRef<HTMLInputElement>(null);

  const [selectedValue, setSelectedValue] = useState<object | null>(value ?? null);
  const [searchList, setSearchList] = useState<any[]>(value ? [value] : []);
  const [inputValue, setInputValue] = useState(() => (value ? (labelFn(value) ?? '') : ''));
  const [showLoader, setShowLoader] = useState(false);

  const isLoading = useRef(true);
  const canLoadData = useRef(true);
  const [searchInputState, setSearchInputState] = useState<XsAutocompleteWithoutLoadSearchingStateTypes>(
    XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORINPUT
  );

  const debouncedValue = useDebounce(inputValue, 500);

  const handleLoad = useCallback(async () => {
    isLoading.current = false;
    if (value && canLoadData.current) {
      setSelectedValue(value);
      setSearchList([value]);
      setInputValue(labelFn(value) ?? '');
      if (onApply) onApply(value, true);
      canLoadData.current = false;
    }
  }, [value]);

  useEffect(() => {
    setSelectedValue(null);
  }, [resetValue]);

  const handleSearch = useCallback(async () => {
    if (!isLoading.current) {
      if (debouncedValue) {
        let data = [];
        if (debouncedValue?.length >= minLengthForSearch) {
          setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORDB);
          setShowLoader(true);
          const response = await axios.post<any>(uri, search(debouncedValue), axiosConfig);
          data = response?.data?.rows ?? [];
          if (!!sortDataList) {
            data = sortDataList(data);
          }
          setShowLoader(false);
          if (data.length > 0) {
            setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.SHOWDATA);
          } else if (response?.data?.executed === false) {
            setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.NOSEARCH);
          } else {
            setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.NODATA);
          }
        }
        setSearchList([...data]);
        if (onSearchAction) {
          onSearchAction(data);
        }
      } else {
        setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORINPUT);
        setSelectedValue(null);
        if (onSearchAction) {
          onSearchAction(null);
        }
        setShowLoader(false);
      }
    }
  }, [debouncedValue]);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  const tooltipText = labelFn(selectedValue) ?? '';

  useEffect(() => {
    if (!!forceSearchString) {
      handleForceSearch(forceSearchString);
    }
  }, [forceSearchString]);

  useEffect(() => {
    if (!!forceSearchSetValue) {
      const forceVal = forceSearchSetValue.object_data;
      const new_value = forceVal;
      setSelectedValue(new_value);
      form.setValue(name, new_value ? identFn(new_value) : null, { shouldValidate: true });
      if (onApply) onApply(new_value, false);
      if (searchAfterForceSetValue) {
        const adress = `${forceVal?.municipality_name} ${forceVal?.street_name} ${forceVal?.property_registration_number}`;
        handleForceSearch(searchAfterForceSetValue(adress));
      }
      if (clearForceSearchValue) clearForceSearchValue();
    }
  }, [forceSearchSetValue]);

  const handleForceSearch = async (searchString: string) => {
    if (!isLoading.current) {
      if (searchString) {
        let data = [];
        setShowLoader(true);
        setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORDB);
        const response = await axios.post<any>(uri, searchString, axiosConfig);
        data = response?.data?.rows ?? [];
        if (!!sortDataList) {
          data = sortDataList(data);
        }
        setShowLoader(false);
        setSearchList(data);
        if (onSearchAction) {
          onSearchAction(data);
        }

        if (data.length > 0) {
          setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.SHOWDATA);
        } else if (response?.data?.executed === false) {
          setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.NOSEARCH);
        } else {
          setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.NODATA);
        }

        if (inputReference) {
          inputReference?.current?.focus();
        }
      } else {
        setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORINPUT);
      }
    }
  };

  return (
    <Autocomplete
      clearText={t('xs_autocompleteServer.clear_text')}
      openText={t('xs_autocompleteServer.open_text')}
      closeText={t('xs_autocompleteServer.close_text')}
      noOptionsText={
        getNoOptionsTextFromState
          ? getNoOptionsTextFromState(searchInputState)
          : noOptionsText
            ? noOptionsText
            : t('xs_autocompleteServer.no_options_text')
      }
      fullWidth={true}
      openOnFocus={true}
      loading={showLoader}
      loadingText={getNoOptionsTextFromState ? getNoOptionsTextFromState(searchInputState) : t('global.searching')}
      options={searchList ?? []}
      disabled={disabled}
      filterOptions={(options) => {
        return options;
      }}
      getOptionLabel={(option) => labelFn(option) ?? ''}
      value={selectedValue ?? null}
      onChange={(_, v) => {
        canLoadData.current = false;
        setSelectedValue(v);
        form.setValue(name, v ? identFn(v) : null, { shouldValidate: true });
        if (onApply) onApply(v, false);
      }}
      renderInput={(params) => (
        <Tooltip title={tooltipText}>
          <form noValidate>
            <TextField
              {...params}
              inputRef={inputReference}
              placeholder={placeholder ?? ''}
              required={required}
              size='small'
              label={label}
              fullWidth={true}
              InputLabelProps={{ shrink: true }}
              value={inputValue}
              onChange={(e) => {
                if (e.target.value?.length >= minLengthForSearch) {
                  setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.WATINGFORDELAY);
                } else {
                  setSearchInputState(XsAutocompleteWithoutLoadSearchingStateTypes.CHARACTERMINLENGTH);
                }

                setInputValue(e.target.value);
                if (e.target.value !== '') {
                  setShowLoader(true);
                }
              }}
              error={error}
              helperText={helperText}
              inputProps={{
                ...params.inputProps,
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              }}
            />
          </form>
        </Tooltip>
      )}
    />
  );
};
